import { useEffect, useState } from "react";
import {
  AlertTriangleIcon,
  GraduationCap,
  PlusIcon,
  XIcon,
} from "lucide-react";
import { Input } from "@component/input";
import { Textarea } from "@component/textarea";
import { Education as EducationType } from "@type/cv";
import { Button } from "@component/button";
import ExperienceTag, { filterTagsByContext, TagType } from "@component/experience-tag";
import { cn } from "@util/tailwind";

interface Props {
  isEditing: boolean;
  isDisabled: boolean;
  data: EducationType[];
  onChange: (values: EducationType[]) => void;
  onEdit: () => void;
}

export function Education(props: Props) {
  const [education, setEducation] = useState<EducationType[]>([]);
  const [newCourse, setNewCourse] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    setEducation(props.data);
  }, []);

  useEffect(() => {
    props.onChange(education);
  }, [education]);

  const handleEducationChange = (
    index: number,
    field: string,
    value: string
  ) => {
    const updatedExperiences = [...education];
    // @ts-ignore
    updatedExperiences[index][field] = value;
    setEducation(updatedExperiences);
  };

  const handleAddCourse = (index: number) => {
    if (newCourse.get(index)?.trim()) {
      setEducation((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          relevant_courses: [
            ...(updated[index].relevant_courses || []),
            newCourse.get(index)?.trim() || "",
          ],
        };
        return updated;
      });
      setNewCourse((prev) => new Map(prev).set(index, ""));
    }
  };

  const handleRemoveCourse = (educationIndex: number, courseIndex: number) => {
    setEducation((prev) => {
      const updated = [...prev];
      updated[educationIndex].relevant_courses = updated[educationIndex].relevant_courses.filter(
        (_, i) => i !== courseIndex
      );
      return updated;
    });
    setNewCourse(new Map());
  };

  const addEducation = () => {
    setEducation([
      ...education,
      {
        degree: "",
        description: "",
        institution: "",
        location: "",
        period: "",
        relevant_courses: [],
        tags: [],
      },
    ]);
  };

  const removeEducation = (index: number) => {
    setEducation(education.filter((_, i: number) => i !== index));
  };

  return (
    <div className="sm:p-6 py-6">
      <div>
        {!props.isEditing && education.length === 0 && (
          <Button
            onClick={props.onEdit}
            disabled={props.isDisabled}
            variant="ghost"
            className="flex items-center text-blue-500 hover:text-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Certification
          </Button>
        )}

        {education.map((edu, index: number) => (
          <div key={index} className="relative flex items-start gap-4">
            <div className="flex-1">
              {props.isEditing ? (
                <div className="space-y-4 mb-6" key={index}>
                  <button
                    onClick={() => removeEducation(index)}
                    className="absolute right-0 top-0 text-red-500 hover:text-red-700"
                    id={`zpne0x_${index}`}
                  >
                    <XIcon className="h-5 w-5" id={`f5f2o1_${index}`} />
                  </button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-black mb-1 block">
                        Degree
                      </label>
                      <Input
                        name="degree"
                        value={edu.degree}
                        onChange={(e) =>
                          handleEducationChange(index, "degree", e.target.value)
                        }
                        className="border-gray-300 text-black"
                      />
                      {edu.degree === "" && (
                        <div className="flex flex-row items-center mt-2 space-x-1">
                          <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                          <p className="text-red-600 text-sm">
                            Degree is required.
                          </p>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-sm text-black mb-1 block">
                        Period
                      </label>
                      <Input
                        name="period"
                        value={edu.period}
                        onChange={(e) =>
                          handleEducationChange(
                            index,
                            "period",
                            e.target.value
                          )
                        }
                        className="border-gray-300 text-black"
                      />
                      {edu.period === "" && (
                        <div className="flex flex-row items-center mt-2 space-x-1">
                          <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                          <p className="text-red-600 text-sm">
                            Period is required.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-black mb-1 block">
                      Institution
                    </label>
                    <Input
                      name="institution"
                      value={edu.institution}
                      onChange={(e) =>
                        handleEducationChange(
                          index,
                          "institution",
                          e.target.value
                        )
                      }
                      className="border-gray-300 text-black"
                    />
                    {edu.institution === "" && (
                      <div className="flex flex-row items-center mt-2 space-x-1">
                        <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                        <p className="text-red-600 text-sm">
                          Institution is required.
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-black mb-1 block">
                      Location
                    </label>
                    <Input
                      name="location"
                      value={edu.location}
                      onChange={(e) =>
                        handleEducationChange(index, "location", e.target.value)
                      }
                      className="border-gray-300 text-black"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-black mb-1 block">
                      Description
                    </label>
                    <Textarea
                      name="description"
                      value={edu.description}
                      onChange={(e) =>
                        handleEducationChange(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                      className="border-gray-300 text-black"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-black mb-1 block">
                      Relevant Courses
                    </label>
                    {edu.relevant_courses.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {edu.relevant_courses.map((course, courseIndex) => (
                          <span
                            key={courseIndex}
                            className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-3 py-1 rounded-full text-sm"
                          >
                            {course}
                            <button
                              onClick={() => handleRemoveCourse(index, courseIndex)}
                              className="ml-2 text-gray-500 hover:text-gray-900"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Input
                        value={newCourse.get(index) || ""}
                        placeholder="Add a course"
                        onChange={(e) => {
                          const value = e.target.value;
                          setNewCourse((prev) => new Map(prev).set(index, value));
                        }}
                        className="border-gray-300 text-black"
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddCourse(index);
                          }
                        }}
                      />

                      <button
                        onClick={() => handleAddCourse(index)}
                        className="px-2 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div key={index} className={cn("relative pl-12", {
                  "pb-8": index < education.length - 1,
                })}>
                  {/* Timeline line - only show for non-last items */}
                  {index < education.length - 1 && (
                    <div className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30" style={{
                      backgroundColor: "#dcfce7",
                    }}></div>
                  )}
                  {/* Icon */}
                  <div className="absolute left-0 top-1">
                    <div
                      className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center z-10 border-2 border-blue-500 dark:border-blue-400"
                      style={{
                        backgroundColor: "#dcfce7",
                        border: "2px solid #22c55e",
                      }}
                    >
                      <GraduationCap
                        className="h-3 w-3 text-blue-600 dark:text-blue-400"
                        style={{
                          color: "#22c55e",
                          border: "0px solid #22c55e",
                        }}
                      />
                    </div>
                  </div>
                  {/* Content */}
                  <div>
                    <div className="mb-1">
                      <div className="flex sm:flex-row flex-col flex-wrap items-start sm:items-center gap-2 relative">
                        <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left">
                          {edu.degree}
                        </h5>
                        {/* Tags moved to be beside the degree - filter for education context only */}
                        {edu.tags && edu.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {filterTagsByContext(
                              edu.tags as TagType[],
                              "education"
                            ).map((tag, tagIndex) => (
                              <ExperienceTag
                                key={tagIndex}
                                type={tag}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      {edu.period && (
                        <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                          {edu.period}
                        </span>
                      )}
                    </div>
                    {edu.institution || edu.location && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                        {edu.institution}{`${edu.location && ` • ${edu.location}`}`}
                      </p>
                    )}
                    {edu.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {edu.description}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {
        props.isEditing && (
          <Button
            onClick={addEducation}
            variant="ghost"
            className="mt-4 flex items-center text-blue-500 hover:text-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Education
          </Button>
        )
      }
    </div>
  );
}
